import {
  createClient,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ConnectionState,
  ConnectionDisconnectedReason,
  UID,
  ClientRole,
  ClientRoleOptions,
  ILocalTrack,
} from 'agora-rtc-react';
import { EventEmitter } from 'events';
import { AGORA_APP_ID } from './rtmEngine';

export class RtcEngine extends EventEmitter {
  instance?: IAgoraRTCClient;
  channelId?: string;

  constructor() {
    super();
    this.instance = createClient({
      mode: 'live',
      codec: 'vp8',
    })();

    this.subscribeClientEvents();
  }

  subscribeClientEvents(): void {
    // user joinned channel
    this.instance?.on('user-joined', (user: IAgoraRTCRemoteUser) => {
      this.emit('user-joined', user);
    });

    // user leave channel
    this.instance?.on(
      'user-left',
      (user: IAgoraRTCRemoteUser, reason: string) => {
        this.emit('user-left', user, reason);
      },
    );

    // connection change
    this.instance?.on(
      'connection-state-change',
      (
        curState: ConnectionState,
        revState: ConnectionState,
        reason: ConnectionDisconnectedReason | undefined,
      ) => {
        this.emit('connection-state-change', curState, revState, reason);
      },
    );

    // user publish video or audio stream
    this.instance?.on(
      'user-published',
      (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
        this.emit('user-published', user, mediaType);
      },
    );

    // user remove video or audio stream
    this.instance?.on(
      'user-unpublished',
      (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => {
        this.emit('user-unpublished', user, mediaType);
      },
    );

    // listen for volume indicator
    this.instance?.on(
      'volume-indicator',
      (result: { level: number; uid: UID }) => {
        this.emit('volume-indicator', result);
      },
    );

    this.instance?.on(
      'user-left',
      (user: IAgoraRTCRemoteUser, reason: string) => {
        this.emit('user-left', user, reason);
      },
    );
  }

  // update client role: Audience or Host
  // Note: This role must be set before publishing the track
  setClientRole(role: ClientRole, option?: ClientRoleOptions): void {
    this.instance?.setClientRole(role, option);
  }

  // join RTC channel
  async joinChannel(
    channelId: string,
    liveUserId?: number | string,
  ): Promise<UID | undefined> {
    if (this.channelId != channelId) {
      this.channelId = channelId;
      if (liveUserId) {
        return this.instance?.join(AGORA_APP_ID, channelId, null, liveUserId);
      }
      return this.instance?.join(AGORA_APP_ID, channelId, null, null);
    }
  }

  // publish track as host role
  publishStream(tracks: ILocalTrack | ILocalTrack[]): void {
    this.instance?.publish(tracks);
  }

  // unpublish track as host role
  async unpublishStream(tracks: ILocalTrack | ILocalTrack[]): Promise<void> {
    await this.instance?.unpublish(tracks);
  }

  // leave current channel
  async leaveChannel(): Promise<void> {
    await this.instance?.leave();
    this.channelId = undefined;
  }

  // destroy rtc instance
  destroy(): void {
    this.instance?.removeAllListeners();
    this.instance?.leave();
    delete this.instance;
  }
}
